import { ref, watchEffect, reactive } from 'vue';
/**
 * Create an asynchronous computed dependency
 * adopted from https://gist.github.com/loilo/fbe3124108a46ff50ab1b867bb5b4bf9
 *
 * @param callback     The promise-returning callback which generates the computed value
 * @param defaultValue A default value, used until the first evaluation finishes
 * @returns A two-item tuple with the first item being a readonly ref to the computed value and the second item holding a boolean ref, indicating whether the async computed value is currently (re-)evaluated
 */
export default function useAsyncComputed(callback, defaultValue) {
  let counter = 0;
  const current = ref(defaultValue);
  const evaluating = ref(false);

  watchEffect(async (onInvalidate) => {
    counter += 1;
    const counterAtBeginning = counter;
    let hasFinished = false;
    try {
      Promise.resolve().then(() => {
        evaluating.value = true;
      });
      const result = await callback((cancelCallback) => {
        onInvalidate(() => {
          evaluating.value = false;
          if (!hasFinished) {
            cancelCallback();
          }
        });
      });
      if (counterAtBeginning === counter) {
        current.value = result;
      }
    } finally {
      evaluating.value = false;
      hasFinished = true;
    }
  });

  const init = async () => {
    if (import.meta.env.SSR) {
      await callback(defaultValue);
    }
  }

  return [current, evaluating, init];
}

export function useAsyncComputedAsReactive(callback, defaultValue) {

  const current = reactive({ d: defaultValue });
  const evaluating = ref(false);
  let running = false;
  watchEffect(async (onInvalidate) => {
    if (running) {
      return;
    }
    running = true;
    let hasFinished = false;
    try {
      Promise.resolve().then(() => {
        evaluating.value = true;
      });
      await callback(current.d, (cancelCallback) => {
        onInvalidate(() => {
          Promise.resolve().then(() => {
            evaluating.value = false;
          });
          if (!hasFinished) {
            cancelCallback();
          }
        });
      });
    } finally {
      running = false;
      evaluating.value = false;
      hasFinished = true;
    }
  });

  const init = async () => {
    if (import.meta.env.SSR) {
      await callback(defaultValue);
    }
  }

  return [current.d, evaluating, init];
}
